// TrackFiji — printed / PDF report (template R1.2, Dropbox › Claude outputs › Report Template R1.2)
// Rendered off-screen at A4-landscape size, measured, split into sheets, and shown only when printing.
import { Fragment, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { TZ } from "../../lib/format";
import { APP_VERSION } from "../../lib/version";
import { avgMoving, coord, fmtMin, fmtSec, type ReportResult } from "../../lib/reports";
import "./report-print.css";

export type PrintObj = { id: string; name: string; org: string | null; group: string | null };

/** Swappable print logo (VR-2.2, cropped + transparent) — replace public/tf-logo.png to change it. */
const LOGO = "/tf-logo.png";

type Col = { label: string; num?: boolean; width?: number };
type Cell = { v: ReactNode; cls?: string };
type Kpi = { label: string; n: ReactNode; unit?: string };
type Model = {
  title: string; metas: [string, string][]; kpis: Kpi[];
  pre?: ReactNode;                          // e.g. activity strip (first sheet only)
  heading: string; note?: string;
  cols: Col[]; rows: Cell[][]; total?: { label: string; span: number; cells: Cell[] };
};

// ---------------------------------------------------------------- formatting (Fiji time)
const dayDate = (d: string) => new Date(`${d}T12:00:00+12:00`);
const fmtDay = (d: string) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(dayDate(d)).replace(",", "");
function fmtPeriod(a: string, b: string) {
  if (a === b) return fmtDay(a);
  const [ya, ma] = a.split("-"), [yb, mb] = b.split("-");
  const f = (d: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, ...o }).format(dayDate(d));
  if (ya === yb && ma === mb) return `${f(a, { day: "numeric" })} – ${f(b, { day: "numeric", month: "short", year: "numeric" })}`;
  if (ya === yb) return `${f(a, { day: "numeric", month: "short" })} – ${f(b, { day: "numeric", month: "short", year: "numeric" })}`;
  return `${f(a, { day: "numeric", month: "short", year: "numeric" })} – ${f(b, { day: "numeric", month: "short", year: "numeric" })}`;
}
const hhmm = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));
const dayHhmm = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)).replace(",", "");
const generated = () => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date());
const km = (n: number) => (Math.round(n * 10) / 10).toFixed(1);
const where = (addr: string | null | undefined, ll?: [number, number]) => addr ?? (ll ? coord(ll[0], ll[1]) : "—");

function hm(min: number): ReactNode {
  if (min < 60) return <>{min}<small>min</small></>;
  return <>{Math.floor(min / 60)}<small className="u-h">h</small>{String(min % 60).padStart(2, "0")}<small className="u-m">min</small></>;
}

// ---------------------------------------------------------------- build the page model for each report type
function buildModel(r: ReportResult, objs: PrintObj[], fromDay: string, toDay: string): Model {
  const byId = new Map(objs.map((o) => [o.id, o]));
  const devs = r.devices.map((d) => byId.get(d.id) ?? { id: d.id, name: d.name, org: null, group: null });
  const orgs = new Set(devs.map((d) => d.org ?? "—"));
  const multiOrg = orgs.size > 1;
  const customer = multiOrg ? "Multiple customers" : [...orgs][0] ?? "—";
  const single = devs.length === 1;
  const period = fmtPeriod(fromDay, toDay);
  const oneDay = fromDay === toDay;
  const metas: [string, string][] = single
    ? [["Customer", customer], ["Object", devs[0].name], ["Period · UTC+12", period]]
    : [["Customer", customer], ["Period", period], ["Time zone", "Fiji · UTC+12"]];
  const orgOf = (row: Record<string, any>) => byId.get(row.deviceId)?.org ?? "—";
  const t = r.totals;
  const custCol: Col[] = multiOrg ? [{ label: "Customer" }] : [];
  const custCell = (row: Record<string, any>): Cell[] => (multiOrg ? [{ v: orgOf(row), cls: "dim" }] : []);
  const objCol: Col[] = single ? [] : [{ label: "Object" }];
  const objCell = (row: Record<string, any>): Cell[] => (single ? [] : [{ v: <b>{row.device}</b> }]);
  const when = oneDay ? hhmm : dayHhmm;

  const dailyKpis = (distanceKm: number, trips: number, drivingMin: number, maxKmh: number): Kpi[] => [
    { label: "Distance", n: km(distanceKm), unit: "km" },
    { label: "Trips", n: trips },
    { label: "Driving time", n: hm(drivingMin) },
    { label: "Top speed", n: maxKmh, unit: "km/h" },
    { label: "Avg moving", n: avgMoving(distanceKm, drivingMin).toFixed(1), unit: "km/h" },
  ];

  if (r.type === "daily" && !single) {
    // Fleet summary: one row per object over the whole period, highest distance first
    const agg = new Map<string, { name: string; id: string; distanceKm: number; trips: number; drivingMin: number; maxKmh: number }>();
    for (const d of devs) agg.set(d.id, { name: d.name, id: d.id, distanceKm: 0, trips: 0, drivingMin: 0, maxKmh: 0 });
    for (const row of r.rows) {
      const a = agg.get(row.deviceId) ?? [...agg.values()].find((x) => x.name === row.device);
      if (!a) continue;
      a.distanceKm += row.distanceKm; a.trips += row.trips; a.drivingMin += row.drivingMin; a.maxKmh = Math.max(a.maxKmh, row.maxKmh);
    }
    const list = [...agg.values()].sort((x, y) => y.distanceKm - x.distanceKm || x.name.localeCompare(y.name));
    const total = list.reduce((s, x) => s + x.distanceKm, 0);
    const trips = list.reduce((s, x) => s + x.trips, 0), drv = list.reduce((s, x) => s + x.drivingMin, 0), top = list.reduce((m, x) => Math.max(m, x.maxKmh), 0);
    const active = list.filter((x) => x.distanceKm > 0 || x.trips > 0).length;
    return {
      title: "Fleet summary", metas,
      kpis: [
        { label: "Fleet distance", n: km(total), unit: "km" },
        { label: "Active objects", n: <>{active}<small>of {list.length}</small></> },
        { label: "Total trips", n: trips },
        { label: "Driving time", n: hm(drv) },
        { label: "Fleet top speed", n: top, unit: "km/h" },
      ],
      heading: "By object", note: "Sorted by distance, highest first",
      cols: [{ label: "Object" }, ...custCol, { label: "Group" }, { label: "Share of distance", width: 200 }, { label: "Distance km", num: true },
        { label: "Trips", num: true }, { label: "Driving", num: true }, { label: "Max km/h", num: true }, { label: "Avg km/h", num: true }],
      rows: list.map((x) => [
        { v: <b>{x.name}</b> }, ...(multiOrg ? [{ v: byId.get(x.id)?.org ?? "—", cls: "dim" }] : []),
        { v: byId.get(x.id)?.group ?? "—", cls: "dim" },
        { v: <div className="share"><i style={{ width: `${total ? (x.distanceKm / total) * 100 : 0}%` }} /></div> },
        { v: km(x.distanceKm), cls: "num" }, { v: x.trips, cls: "num" }, { v: fmtMin(x.drivingMin), cls: "num" },
        { v: x.maxKmh, cls: "num" }, { v: avgMoving(x.distanceKm, x.drivingMin).toFixed(1), cls: "num" },
      ]),
      total: { label: "Fleet total", span: multiOrg ? 4 : 3, cells: [
        { v: km(total), cls: "num" }, { v: trips, cls: "num" }, { v: fmtMin(drv), cls: "num" }, { v: top, cls: "num" },
        { v: avgMoving(total, drv).toFixed(1), cls: "num" }] },
    };
  }

  if (r.type === "daily") {
    const kpis = dailyKpis(t.distanceKm ?? 0, t.trips ?? 0, t.drivingMin ?? 0, t.maxKmh ?? 0);
    if (r.dayTrips) {
      const dayStart = Date.parse(`${fromDay}T00:00:00+12:00`);
      const segs = r.dayTrips.map((x) => {
        const left = Math.max(0, (Date.parse(x.start) - dayStart) / 60000) / 1440;
        const width = Math.max(x.durationMin, 1) / 1440;
        return { left: Math.min(left, 1) * 100, width: Math.min(width, 1 - Math.min(left, 1)) * 100 };
      });
      return {
        title: "Daily summary", metas, kpis,
        pre: (
          <div className="blk">
            <div className="sec-h">
              <h2>Activity across the day</h2>
              <div className="legend"><span><i className="sw" />Driving</span><span><i className="sw off" />Stopped / offline</span></div>
            </div>
            <div className="timeline">{segs.map((s, i) => <div key={i} className="seg" style={{ left: `${s.left}%`, width: `${s.width}%` }} />)}</div>
            <div className="ticks">{[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => <span key={h}>{String(h).padStart(2, "0")}:00</span>)}</div>
          </div>
        ),
        heading: "Trips", note: "Showing all trips for the period",
        cols: [{ label: "#", width: 36 }, { label: "Start" }, { label: "End" }, { label: "From" }, { label: "To" },
          { label: "Distance km", num: true }, { label: "Duration", num: true }, { label: "Max km/h", num: true }],
        rows: r.dayTrips.map((x, i) => [
          { v: String(i + 1).padStart(2, "0"), cls: "mono dim" }, { v: hhmm(x.start), cls: "mono" }, { v: hhmm(x.end), cls: "mono" },
          { v: where(x.startAddress, x.startLatLng), cls: x.startAddress ? "" : "mono small" },
          { v: where(x.endAddress, x.endLatLng), cls: x.endAddress ? "" : "mono small" },
          { v: km(x.distanceKm), cls: "num" }, { v: fmtMin(x.durationMin), cls: "num" }, { v: x.maxKmh, cls: "num" },
        ]),
        total: { label: `Day total · ${r.dayTrips.length} trip${r.dayTrips.length === 1 ? "" : "s"}`, span: 5, cells: [
          { v: km(t.distanceKm ?? 0), cls: "num" }, { v: fmtMin(t.drivingMin ?? 0), cls: "num" }, { v: t.maxKmh ?? 0, cls: "num" }] },
      };
    }
    // one object, several days
    return {
      title: "Daily summary", metas, kpis, heading: "By day",
      cols: [{ label: "Date" }, { label: "Distance km", num: true }, { label: "Trips", num: true }, { label: "Driving", num: true },
        { label: "Max km/h", num: true }, { label: "Avg km/h", num: true }],
      rows: r.rows.map((x) => [
        { v: fmtDay(x.date) }, { v: km(x.distanceKm), cls: "num" }, { v: x.trips, cls: "num" }, { v: fmtMin(x.drivingMin), cls: "num" },
        { v: x.maxKmh, cls: "num" }, { v: Number(x.avgKmh).toFixed(1), cls: "num" },
      ]),
      total: { label: "Period total", span: 1, cells: [
        { v: km(t.distanceKm ?? 0), cls: "num" }, { v: t.trips ?? 0, cls: "num" }, { v: fmtMin(t.drivingMin ?? 0), cls: "num" },
        { v: t.maxKmh ?? 0, cls: "num" }, { v: avgMoving(t.distanceKm ?? 0, t.drivingMin ?? 0).toFixed(1), cls: "num" }] },
    };
  }

  if (r.type === "trips") {
    return {
      title: "Trips", metas, kpis: dailyKpis(t.distanceKm ?? 0, t.trips ?? 0, t.drivingMin ?? 0, t.maxKmh ?? 0),
      heading: "Trips", note: single ? "Showing all trips for the period" : "Grouped by object",
      cols: [...objCol, ...custCol, { label: "Start" }, { label: "End" }, { label: "From" }, { label: "To" },
        { label: "Distance km", num: true }, { label: "Duration", num: true }, { label: "Max km/h", num: true }],
      rows: r.rows.map((x) => [...objCell(x), ...custCell(x), { v: when(x.start), cls: "mono" }, { v: when(x.end), cls: "mono" },
        { v: where(x.startAddress, x.startLatLng), cls: x.startAddress ? "" : "mono small" },
        { v: where(x.endAddress, x.endLatLng), cls: x.endAddress ? "" : "mono small" },
        { v: km(x.distanceKm), cls: "num" }, { v: fmtMin(x.durationMin), cls: "num" }, { v: x.maxKmh, cls: "num" }]),
      total: { label: `Total · ${r.rows.length} trip${r.rows.length === 1 ? "" : "s"}`, span: objCol.length + custCol.length + 4, cells: [
        { v: km(t.distanceKm ?? 0), cls: "num" }, { v: fmtMin(t.drivingMin ?? 0), cls: "num" }, { v: t.maxKmh ?? 0, cls: "num" }] },
    };
  }

  if (r.type === "stops") {
    const longest = r.rows.reduce((m, x) => Math.max(m, x.durationMin), 0);
    const avg = r.rows.length ? Math.round((t.stoppedMin ?? 0) / r.rows.length) : 0;
    return {
      title: "Stops", metas,
      kpis: [
        { label: "Stops", n: t.stops ?? 0 }, { label: "Time stopped", n: hm(t.stoppedMin ?? 0) },
        { label: "Longest stop", n: hm(longest) }, { label: "Average stop", n: hm(avg) },
        { label: "Objects", n: new Set(r.rows.map((x) => x.device)).size, unit: `of ${devs.length}` },
      ],
      heading: "Stops",
      cols: [...objCol, ...custCol, { label: "Arrived" }, { label: "Left" }, { label: "Stopped for", num: true }, { label: "Location" }],
      rows: r.rows.map((x) => [...objCell(x), ...custCell(x), { v: when(x.start), cls: "mono" }, { v: when(x.end), cls: "mono" },
        { v: fmtMin(x.durationMin), cls: "num" }, { v: x.address ?? coord(x.lat, x.lng), cls: x.address ? "" : "mono small" }]),
      total: { label: `Total · ${r.rows.length} stop${r.rows.length === 1 ? "" : "s"}`, span: objCol.length + custCol.length + 2, cells: [
        { v: fmtMin(t.stoppedMin ?? 0), cls: "num" }, { v: "" }] },
    };
  }

  // speeding
  const overSec = r.rows.reduce((s, x) => s + x.durationSec, 0);
  return {
    title: `Speeding over ${r.limit} km/h`, metas,
    kpis: [
      { label: "Speeding events", n: t.events ?? 0 }, { label: "Top speed", n: t.maxKmh ?? 0, unit: "km/h" },
      { label: "Time over limit", n: overSec < 60 ? <>{overSec}<small>s</small></> : hm(Math.round(overSec / 60)) },
      { label: "Objects involved", n: new Set(r.rows.map((x) => x.device)).size, unit: `of ${devs.length}` },
      { label: "Speed limit", n: r.limit ?? 0, unit: "km/h" },
    ],
    heading: "Speeding events",
    cols: [...objCol, ...custCol, { label: "Started" }, { label: "Duration", num: true }, { label: "Top speed km/h", num: true }, { label: "Where (top speed)" }],
    rows: r.rows.map((x) => [...objCell(x), ...custCell(x), { v: when(x.start), cls: "mono" }, { v: fmtSec(x.durationSec), cls: "num" },
      { v: x.maxKmh, cls: "num" }, { v: coord(x.lat, x.lng), cls: "mono small" }]),
  };
}

// ---------------------------------------------------------------- component
export default function ReportPrint({ res, objs, fromDay, toDay }: { res: ReportResult; objs: PrintObj[]; fromDay: string; toDay: string }) {
  const model = useMemo(() => buildModel(res, objs, fromDay, toDay), [res, objs, fromDay, toDay]);
  const stamp = useMemo(() => generated(), [res]); // eslint-disable-line react-hooks/exhaustive-deps
  const measureRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<number[][] | null>(null); // row indexes per sheet
  const [compact, setCompact] = useState(false);               // tighter rows when that keeps the report on one sheet

  useLayoutEffect(() => { setPages(null); setCompact(false); }, [model]);
  useLayoutEffect(() => {
    if (pages) return;
    let cancelled = false;
    const run = () => {
      const root = measureRef.current;
      if (!root || cancelled) return;
      const sheet = root.querySelector<HTMLElement>(".sheet")!;
      const cs = getComputedStyle(sheet);
      const inner = sheet.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const gap = parseFloat(cs.rowGap || "22") || 22;
      const h = (sel: string) => root.querySelector<HTMLElement>(sel)?.offsetHeight ?? 0;
      const fixed = h(".sec-h.tbl") + h("thead") + h("footer.foot");
      const rows = [...root.querySelectorAll<HTMLElement>("tbody tr.r")].map((tr) => tr.offsetHeight);
      const totalH = h("tbody tr.total");
      const first = inner - h("header") - h(".kpis") - (model.pre ? h(".pre") + gap : 0) - fixed - gap * 3 - 6;
      const rest = inner - h("header") - fixed - gap * 2 - 6;
      const out: number[][] = [[]];
      let room = first;
      rows.forEach((rh, i) => {
        if (rh > room && out[out.length - 1].length) { out.push([]); room = rest; }
        out[out.length - 1].push(i); room -= rh;
      });
      if (model.total && totalH > room) out.push([]); // total row on its own sheet if it doesn't fit
      if (out.length > 1 && !compact) {
        // Would 5 px less padding per row (compact) fit everything on the first sheet?
        const need = rows.reduce((a, b) => a + b - 5, 0) + (model.total ? totalH - 4 : 0);
        if (need <= first) { setCompact(true); return; }   // re-measure in compact mode
      }
      setPages(out);
    };
    // wait for the print fonts so row heights are final
    (document.fonts?.ready ?? Promise.resolve()).then(() => requestAnimationFrame(run));
    return () => { cancelled = true; };
  }, [pages, model, compact]);

  const sheetsIdx = pages ?? [model.rows.map((_, i) => i)];
  return (
    <div className={`rep-print ${pages ? "" : "measuring"} ${compact ? "compact" : ""}`} ref={measureRef} aria-hidden="true">
      {sheetsIdx.map((idx, p) => (
        <Sheet key={p} model={model} first={p === 0} last={p === sheetsIdx.length - 1} rowIdx={idx}
          page={p + 1} pages={sheetsIdx.length} stamp={stamp} />
      ))}
    </div>
  );
}

function Sheet({ model, first, last, rowIdx, page, pages, stamp }: {
  model: Model; first: boolean; last: boolean; rowIdx: number[]; page: number; pages: number; stamp: string;
}) {
  return (
    <section className="sheet">
      <header>
        <div className="rh-top">
          <div className="rh-brand">
            <img src={LOGO} alt="Tracking Fiji" />
            <div className="rh-div" />
            <div><div className="eyebrow">Fleet report</div><h1>{model.title}</h1></div>
          </div>
          <div className="rh-meta">
            {model.metas.map(([l, v]) => (
              <div key={l} className={l === "Customer" || (l === "Object" && String(v).length > 18) ? "cust" : undefined}>
                <span className="lbl">{l}</span>
                <span className={`val ${(l === "Customer" || l === "Object") && String(v).length > 22 ? "long" : ""}`} title={String(v)}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rule"><i /><b /></div>
      </header>

      {first && (
        <div className="kpis">
          {model.kpis.map((k, i) => (
            <div key={k.label} className={`kpi ${i === 0 ? "hi" : ""}`}>
              <span className="lbl">{k.label}</span>
              <span className="n">{k.n}{k.unit && <small>{k.unit}</small>}</span>
            </div>
          ))}
        </div>
      )}
      {first && model.pre && <div className="pre">{model.pre}</div>}

      <div className="blk">
        <div className="sec-h tbl"><h2>{model.heading}{!first && <span className="cont"> (continued)</span>}</h2>{model.note && <span className="note">{model.note}</span>}</div>
        <table>
          <thead><tr>{model.cols.map((c) => <th key={c.label} className={c.num ? "num" : ""} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rowIdx.map((i) => (
              <tr key={i} className="r">{model.rows[i].map((c, j) => <td key={j} className={c.cls}>{c.v}</td>)}</tr>
            ))}
            {!model.rows.length && first && <tr className="r"><td colSpan={model.cols.length} className="dim">Nothing found for this period.</td></tr>}
            {last && model.total && (
              <tr className="total">
                {model.title === "Daily summary" && model.cols[0]?.label === "#" ? <><td /><td colSpan={model.total.span - 1} className="t">{model.total.label}</td></>
                  : <td colSpan={model.total.span} className="t">{model.total.label}</td>}
                {model.total.cells.map((c, j) => <Fragment key={j}><td className={c.cls}>{c.v}</td></Fragment>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="spacer" />
      <footer className="foot">
        <div>Generated {stamp} (Fiji) · Tracking Fiji, operated by Skyward Industries Pte Ltd</div>
        <div className="r"><span>trackingfiji.com</span><span>R{APP_VERSION.replace(/^V/, "")}</span><span>Page {page} of {pages}</span></div>
      </footer>
    </section>
  );
}
